import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { normalizeFontFamily } from '../utils/fontFamilyResolver';
import { useFurigana, useFuriganaAria } from '../contexts/FuriganaContext';
import FuriganaText from './FuriganaText';

// Component-supplied furigana for UI labels whose reading is role-fixed and NOT derivable from the lone token
// by the (stimulus-safe, universal-only) engine: the 小/中/大 font-size scale reads しょう/ちゅう/だい (a bare 中
// otherwise defaults to the "inside" reading なか), and the color labels' 色 is the everyday いろ, not the
// compound-suffix しょく. Passed as <FuriganaText overrides={...}> so the reading is applied only to THIS label,
// never to uploaded stimulus (方案C, Codex R16). En-locale labels have no matching kanji, so the map is a no-op there.
const SIZE_FURIGANA = { 小: 'しょう', 中: 'ちゅう', 大: 'だい' };
const COLOR_LABEL_FURIGANA = { 色: 'いろ' };

const DEFAULT_SETTINGS = {
  fontFamily: "'UDデジタル教科書体', sans-serif",
  fontSize: 16,
  letterSpacing: 0.05,
  textColor: '#000000',
  voiceInputEnabled: true,
  voiceOutputEnabled: true,
  voiceType: 'female_neural',
  speechRate: 1.0,
  speechVolume: 1.0,
  highlightAccentColor: '#facc15',
  problemTextHighlightColor: '#facc15',
  colorAccessibilityPreset: 'auto',
  attentionAnimationSpeed: 1.0,
};

// Merge incoming settings over defaults, canonicalizing the (possibly legacy
// English DB-default) font family so the <select> value matches a real
// <option> instead of falling through to the browser default.
const mergeSettings = (incoming) => {
  const merged = { ...DEFAULT_SETTINGS, ...incoming };
  return { ...merged, fontFamily: normalizeFontFamily(merged.fontFamily) };
};

const SettingsPanel = ({ isOpen, onClose, settings, onSettingsChange }) => {
  const { t } = useTranslation();
  const { enabled: furiganaEnabled, toggle: toggleFurigana } = useFurigana();
  const fa = useFuriganaAria();
  const [localSettings, setLocalSettings] = useState(mergeSettings(settings));

  useEffect(() => {
    setLocalSettings(mergeSettings(settings));
  }, [settings, isOpen]);

  const colorPresetOptions = [
    {
      value: 'auto',
      label: t('settings.preset.auto.label'),
      description: t('settings.preset.auto.description'),
      colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444']
    },
    {
      value: 'accent_cud',
      label: t('settings.preset.accentCud.label'),
      description: t('settings.preset.accentCud.description'),
      colors: ['#FF2800', '#FAF500', '#35A16B', '#0041FF']
    },
    {
      value: 'base_cud',
      label: t('settings.preset.baseCud.label'),
      description: t('settings.preset.baseCud.description'),
      colors: ['#FFD1D1', '#FFFF99', '#CBF266', '#B4EBFA']
    },
    {
      value: 'neutral_cud',
      label: t('settings.preset.neutralCud.label'),
      description: t('settings.preset.neutralCud.description'),
      colors: ['#FFFFFF', '#C8C8CB', '#7F878F', '#000000']
    }
  ];

  const handleChange = (key, value) => {
    const newSettings = { ...localSettings, [key]: value };
    setLocalSettings(newSettings);
    onSettingsChange(newSettings);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm px-4" style={{ backgroundColor: 'rgba(30,45,56,0.4)' }}>
      <div className="w-full max-w-7xl rounded-[28px] border p-6 max-h-[90vh] overflow-y-auto" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-surface)', boxShadow: '0 24px 60px rgba(61,90,110,0.15)' }}>
        {/* ヘッダー */}
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold" style={{ color: 'var(--primary)' }}><FuriganaText>⚙️ {t('settings.title')}</FuriganaText></h2>
          <button
            onClick={onClose}
            className="text-3xl font-bold transition-colors"
            aria-label={t('common.close')}
            style={{ color: 'var(--text-muted)' }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--danger)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
          >
            ×
          </button>
        </div>

        {/* 言語切り替え / Language */}
        <div className="mb-6 flex items-center gap-3">
          <span className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
            <FuriganaText text={t('common.language')} />
          </span>
          <LanguageSwitcher />
        </div>

        {/* 設定項目 - 3カラムレイアウト */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* 左列: フォント設定 */}
          <div className="space-y-4">
            <h3 className="mb-3 text-lg font-bold" style={{ color: 'var(--text)' }}><FuriganaText>📝 {t('settings.fontSection')}</FuriganaText></h3>

            {/* フォントファミリー */}
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                <FuriganaText text={t('settings.fontType')} />
              </label>
              <select
                aria-label={t('settings.fontType')}
                value={localSettings.fontFamily}
                onChange={(e) => handleChange('fontFamily', e.target.value)}
                className="w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
                style={{ borderColor: 'var(--border)', '--tw-ring-color': 'var(--primary)' }}
              >
                <optgroup label={fa(t('settings.fontGroup.textbook'))}>
                  <option value="'UDデジタル教科書体', sans-serif">
                    {t('settings.font.udDigi')}
                  </option>
                  <option value="'YuKyokasho', 'YuKyokasho Yoko', 'UDデジタル教科書体', sans-serif">
                    {t('settings.font.yuKyokasho')}
                  </option>
                </optgroup>
                <optgroup label={fa(t('settings.fontGroup.readable'))}>
                  <option value="'Hiragino Sans', 'Hiragino Kaku Gothic ProN', sans-serif">
                    {t('settings.font.hiragino')}
                  </option>
                  <option value="'Yu Gothic', 'YuGothic', 'Hiragino Sans', sans-serif">
                    {t('settings.font.yuGothic')}
                  </option>
                  <option value="'Meiryo', 'Yu Gothic', sans-serif">
                    {t('settings.font.meiryo')}
                  </option>
                  <option value="'MS Gothic', 'MS PGothic', sans-serif">
                    {t('settings.font.msGothic')}
                  </option>
                </optgroup>
                <optgroup label={fa(t('settings.fontGroup.monospace'))}>
                  <option value="'Courier New', 'Consolas', monospace">
                    {t('settings.font.courier')}
                  </option>
                  <option value="'MS Gothic', 'Osaka-Mono', monospace">
                    {t('settings.font.msGothicMono')}
                  </option>
                </optgroup>
              </select>
            </div>

            {/* フォントサイズ */}
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                {t('settings.fontSize')}: {localSettings.fontSize}px
              </label>
              <input
                type="range"
                min="12"
                max="32"
                step="2"
                value={localSettings.fontSize}
                onChange={(e) => handleChange('fontSize', parseInt(e.target.value))}
                aria-label={t('settings.fontSize')}
                className="w-full"
              />
              <div className="mt-1 flex justify-between text-xs" style={{ color: 'var(--text-muted)' }}>
                <span><FuriganaText overrides={SIZE_FURIGANA}>{t('settings.sizeSmall')} (12px)</FuriganaText></span>
                <span><FuriganaText overrides={SIZE_FURIGANA}>{t('settings.sizeMedium')} (20px)</FuriganaText></span>
                <span><FuriganaText overrides={SIZE_FURIGANA}>{t('settings.sizeLarge')} (32px)</FuriganaText></span>
              </div>
            </div>

            {/* 文字間隔 */}
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                <FuriganaText>{t('settings.letterSpacing')}: {localSettings.letterSpacing || 0.05}em</FuriganaText>
              </label>
              <input
                type="range"
                min="0"
                max="0.3"
                step="0.05"
                value={localSettings.letterSpacing || 0.05}
                onChange={(e) => handleChange('letterSpacing', parseFloat(e.target.value))}
                aria-label={t('settings.letterSpacing')}
                className="w-full"
              />
              <div className="mt-1 flex justify-between text-xs" style={{ color: 'var(--text-muted)' }}>
                <span><FuriganaText>{t('settings.spacingNarrow')} (0em)</FuriganaText></span>
                <span><FuriganaText>{t('settings.spacingStandard')} (0.05em)</FuriganaText></span>
                <span><FuriganaText>{t('settings.spacingWide')} (0.3em)</FuriganaText></span>
              </div>
            </div>

            {/* テキストカラー */}
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                {t('settings.textColor')}
              </label>
              <div className="flex gap-3">
                <input
                  type="color"
                  aria-label={t('settings.textColor')}
                  value={localSettings.textColor}
                  onChange={(e) => handleChange('textColor', e.target.value)}
                  className="h-10 w-16 cursor-pointer rounded border"
                  style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-surface)' }}
                />
                <input
                  type="text"
                  aria-label={t('settings.textColor')}
                  value={localSettings.textColor}
                  onChange={(e) => handleChange('textColor', e.target.value)}
                  className="flex-1 rounded-lg border px-3 py-2"
                  style={{ borderColor: 'var(--border)' }}
                  placeholder="#000000"
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={furiganaEnabled}
                  onChange={toggleFurigana}
                  className="h-5 w-5 rounded focus:ring-2"
                  style={{ borderColor: 'var(--border)', accentColor: 'var(--primary)', '--tw-ring-color': 'var(--primary)' }}
                />
                <span className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                  <FuriganaText text={t('settings.furiganaToggle')} />
                </span>
              </label>
              <p className="mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
                <FuriganaText text={t('settings.furiganaHint')} />
              </p>
            </div>

          </div>

          {/* 中列: 音声設定 + 視覚ガイド */}
          <div className="space-y-4">
            {/* 音声設定 */}
            <div>
              <h3 className="mb-3 text-lg font-bold" style={{ color: 'var(--text)' }}><FuriganaText>🔊 {t('settings.voiceSection')}</FuriganaText></h3>

              <div className="mb-3">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={localSettings.voiceInputEnabled}
                    onChange={(e) => handleChange('voiceInputEnabled', e.target.checked)}
                    className="h-5 w-5 rounded focus:ring-2"
                    style={{ borderColor: 'var(--border)', accentColor: 'var(--primary)', '--tw-ring-color': 'var(--primary)' }}
                  />
                  <span className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                    <FuriganaText text={t('settings.voiceInput')} />
                  </span>
                </label>
              </div>

              <div className="mb-3">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={localSettings.voiceOutputEnabled}
                    onChange={(e) => handleChange('voiceOutputEnabled', e.target.checked)}
                    className="h-5 w-5 rounded focus:ring-2"
                    style={{ borderColor: 'var(--border)', accentColor: 'var(--primary)', '--tw-ring-color': 'var(--primary)' }}
                  />
                  <span className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                    <FuriganaText text={t('settings.voiceOutput')} />
                  </span>
                </label>
              </div>

              <div className="mb-3">
                <label className="mb-2 block text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                  <FuriganaText text={t('settings.voiceTypeLabel')} />
                </label>
                <select
                  aria-label={t('settings.voiceTypeLabel')}
                  value={localSettings.voiceType}
                  onChange={(e) => handleChange('voiceType', e.target.value)}
                  className="w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
                  style={{ borderColor: 'var(--border)', '--tw-ring-color': 'var(--primary)' }}
                  disabled={!localSettings.voiceOutputEnabled}
                >
                  <optgroup label={fa(t('settings.voiceGroup.neural2'))}>
                    <option value="female_neural">{t('settings.voice.femaleNeural')}</option>
                  </optgroup>
                  <optgroup label={fa(t('settings.voiceGroup.wavenet'))}>
                    <option value="female_wavenet">{t('settings.voice.femaleWavenet')}</option>
                    <option value="male_wavenet">{t('settings.voice.maleWavenet')}</option>
                  </optgroup>
                  <optgroup label={fa(t('settings.voiceGroup.standard'))}>
                    <option value="female_standard">{t('settings.voice.femaleStandard')}</option>
                    <option value="male_standard">{t('settings.voice.maleStandard')}</option>
                  </optgroup>
                </select>
              </div>

              {/* 読み上げ速度 */}
              <div className="mb-3">
                <label className="mb-2 block text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                  <FuriganaText>{t('settings.speechRate')}: {localSettings.speechRate.toFixed(1)}x</FuriganaText>
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="2.0"
                  step="0.1"
                  value={localSettings.speechRate}
                  onChange={(e) => handleChange('speechRate', parseFloat(e.target.value))}
                  aria-label={t('settings.speechRate')}
                  className="w-full"
                  disabled={!localSettings.voiceOutputEnabled}
                />
                <div className="mt-1 flex justify-between text-xs" style={{ color: 'var(--text-muted)' }}>
                  <span><FuriganaText>{t('settings.rateSlow')} (0.5x)</FuriganaText></span>
                  <span><FuriganaText>{t('settings.rateNormal')} (1.0x)</FuriganaText></span>
                  <span><FuriganaText>{t('settings.rateFast')} (2.0x)</FuriganaText></span>
                </div>
              </div>
            </div>

            {/* 視覚ガイド */}
            <div className="border-t pt-4" style={{ borderColor: 'var(--border)' }}>
              <h4 className="mb-3 text-base font-bold" style={{ color: 'var(--text)' }}><FuriganaText>👁️ {t('settings.visualGuide')}</FuriganaText></h4>

              <div className="mb-3">
                <label className="mb-2 block text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                  <FuriganaText>{t('settings.attentionSpeed')}: {(localSettings.attentionAnimationSpeed ?? 1).toFixed(1)}x</FuriganaText>
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="2.5"
                  step="0.1"
                  value={localSettings.attentionAnimationSpeed ?? 1}
                  onChange={(e) => handleChange('attentionAnimationSpeed', parseFloat(e.target.value))}
                  aria-label={t('settings.attentionSpeed')}
                  className="w-full"
                />
                <div className="mt-1 flex justify-between text-xs" style={{ color: 'var(--text-muted)' }}>
                  <span>{t('settings.speedSlow')} (0.5x)</span>
                  <span><FuriganaText>{t('settings.speedStandard')} (1.0x)</FuriganaText></span>
                  <span><FuriganaText>{t('settings.speedFast')} (2.5x)</FuriganaText></span>
                </div>
                <p className="mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                  <FuriganaText text={t('settings.attentionNote')} />
                </p>
              </div>
            </div>
          </div>

          {/* 右列: ハイライト色・CUD */}
          <div className="space-y-4">
            {/* ハイライト色 */}
            <h3 className="mb-3 text-lg font-bold" style={{ color: 'var(--text)' }}><FuriganaText>🎨 {t('settings.colorSection')}</FuriganaText></h3>

            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                <FuriganaText text={t('settings.highlightColor')} overrides={COLOR_LABEL_FURIGANA} />
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  aria-label={t('settings.highlightColor')}
                  value={localSettings.highlightAccentColor || '#facc15'}
                  onChange={(e) => handleChange('highlightAccentColor', e.target.value)}
                  className="h-10 w-16 cursor-pointer rounded border"
                  style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-surface)' }}
                />
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}><FuriganaText text={t('settings.highlightColorHint')} overrides={COLOR_LABEL_FURIGANA} /></span>
              </div>
            </div>

            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                <FuriganaText text={t('settings.problemHighlightColor')} overrides={COLOR_LABEL_FURIGANA} />
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  aria-label={t('settings.problemHighlightColor')}
                  value={localSettings.problemTextHighlightColor || localSettings.highlightAccentColor || '#facc15'}
                  onChange={(e) => handleChange('problemTextHighlightColor', e.target.value)}
                  className="h-10 w-16 cursor-pointer rounded border"
                  style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-surface)' }}
                />
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  <FuriganaText text={t('settings.problemHighlightHint')} />
                </span>
              </div>
            </div>

            {/* カラーユニバーサルデザインプリセット */}
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                {t('settings.cudPresetLabel')}
              </label>
              <div className="space-y-2" role="radiogroup" aria-label={t('settings.cudPresetLabel')}>
                {colorPresetOptions.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-start gap-2 rounded-xl border px-2.5 py-1.5 transition"
                    style={
                      localSettings.colorAccessibilityPreset === option.value
                        ? { borderColor: 'var(--primary)', backgroundColor: 'color-mix(in srgb, var(--primary) 8%, var(--bg-surface))' }
                        : { borderColor: 'var(--border)', backgroundColor: 'color-mix(in srgb, var(--bg-surface) 70%, transparent)' }
                    }
                  >
                    <input
                      type="radio"
                      name="colorAccessibilityPreset"
                      value={option.value}
                      checked={localSettings.colorAccessibilityPreset === option.value}
                      onChange={(e) => handleChange('colorAccessibilityPreset', e.target.value)}
                      className="mt-1 h-4 w-4 focus:ring-2"
                      style={{ accentColor: 'var(--primary)', '--tw-ring-color': 'var(--primary)' }}
                    />
                    <div className="flex-1 space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-semibold" style={{ color: 'var(--text)' }}><FuriganaText text={option.label} /></span>
                        <div className="flex items-center gap-0.5">
                          {option.colors.map((c, idx) => (
                            <span
                              key={`${option.value}-${idx}`}
                              className="h-3 w-3 rounded-full border shadow-sm"
                              style={{ borderColor: 'var(--bg-surface)', backgroundColor: c }}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-[10px] leading-snug" style={{ color: 'var(--text-muted)' }}><FuriganaText text={option.description} /></p>
                    </div>
                  </label>
                ))}
                <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                  <FuriganaText text={t('settings.cudSource')} />
                </p>
              </div>
            </div>

          </div>
        </div>

        {/* リセットボタン */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={() => {
              const defaultSettings = { ...DEFAULT_SETTINGS };
              setLocalSettings(defaultSettings);
              onSettingsChange(defaultSettings);
            }}
            className="flex-1 rounded-full border px-4 py-2 text-sm font-semibold transition-colors"
            style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-surface)', color: 'var(--text-muted)' }}
          >
            <FuriganaText text={t('settings.reset')} />
          </button>
          <button
            onClick={onClose}
            className="flex-1 rounded-full px-4 py-2 text-sm font-semibold shadow-md transition-transform hover:scale-[1.02]"
            style={{ backgroundColor: 'var(--primary)', color: '#FFFFFF' }}
          >
            <FuriganaText text={t('common.close')} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;
